import { jwtDecode } from "jwt-decode";
import axios from "./axios";
import { TOKEN_STORAGE_KEY } from "configs/auth.config";

/**
 * Checks if the provided JWT token is valid (not expired).
 *
 * @param {string} authToken - The JWT token to validate.
 * @returns {boolean} - Returns `true` if the token is valid, otherwise `false`.
 */
const isTokenValid = (authToken) => {
  if (typeof authToken !== "string") {
    return false;
  }

  try {
    const decoded = jwtDecode(authToken);
    const currentTime = Date.now() / 1000; // Current time in seconds since epoch

    return decoded.exp > currentTime;
  } catch {
    return false;
  }
};

/**
 * Recovers the active token, either from the legacy storage or Supabase storage.
 * 
 * @returns {string|null} - The active JWT token or null.
 */
const getActiveToken = () => {
  if (typeof window === 'undefined') return null;

  // 1. Try legacy token
  let token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token && token !== "null" && token !== "undefined") return token;

  // 2. Try Supabase token
  try {
    const supabaseKey = Object.keys(localStorage).find(key => key.includes('-auth-token'));
    if (supabaseKey) {
      const supabaseData = JSON.parse(localStorage.getItem(supabaseKey));
      token = supabaseData?.access_token;
      if (token) {
        return token;
      }
    }
  } catch {
    /* token recovery error */
  }

  return null;
};

/**
 * Sets or removes the authentication token in local storage and axios headers.
 *
 * @param {string} [authToken] - The JWT token to set. If `undefined` or `null`, the session will be cleared.
 */
const setSession = (authToken) => {
  if (typeof authToken === "string" && authToken.trim() !== "" && authToken !== "null") {
    // Store token in local storage and set authorization header for axios
    localStorage.setItem(TOKEN_STORAGE_KEY, authToken);
    axios.defaults.headers.common.Authorization = `Bearer ${authToken}`;
  } else {
    // Remove token from local storage and delete authorization header from axios
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    delete axios.defaults.headers.common.Authorization;
  }
};

/**
 * Gets the current user from the stored token
 *
 * @returns {object|null} - Returns the user data from token or null if no valid token
 */
const getCurrentUser = () => {
  try {
    const token = getActiveToken();
    if (!token || !isTokenValid(token)) {
      return null;
    }

    const decoded = jwtDecode(token);

    // Vérifier que userId, sub ou id existe (support backend qui envoie "id" au lieu de "userId")
    const userId = decoded.sub || decoded.userId || decoded.id;
    if (!userId) {
      return null;
    }

    // Vérifier que l'ID est valide (pas une string commençant par 'uid-')
    if (typeof userId === 'string' && userId.startsWith('uid-')) {
      return null;
    }

    const userData = {
      id: userId,
      email: decoded.email,
      role: decoded.role || decoded.type, // Priorité au rôle, fallback sur type (legacy)
      type: decoded.role || decoded.type, // Maintenir type pour compatibilité pendant la transition
      chauffeur_id: decoded.chauffeur_id,
      exp: decoded.exp,
    };
    return userData;
  } catch {
    return null;
  }
};

/**
 * Checks if the current user has the required permission
 *
 * @param {string} permission - The permission to check
 * @returns {boolean} - Returns true if user has permission
 */
const hasPermission = (permission) => {
  const user = getCurrentUser();
  if (!user) return false;

  // Import permissions dynamically to avoid circular dependency
  import("configs/auth.config")
    .then(({ USER_PERMISSIONS }) => {
      const userPermissions = USER_PERMISSIONS[user.type] || [];
      return userPermissions.includes(permission);
    })
    .catch(() => { /* permission load error */ });

  return false;
};

/**
 * Gets the token expiration time
 *
 * @returns {Date|null} - Returns the expiration date or null
 */
const getTokenExpiration = () => {
  try {
    const token = getActiveToken();
    if (!token) return null;

    const decoded = jwtDecode(token);
    return new Date(decoded.exp * 1000);
  } catch {
    return null;
  }
};

/**
 * Generate a simple JWT-like token for local use
 * Note: This is a simple implementation for development/testing
 * In production, use a proper JWT library with signing
 *
 * @param {object} user - User data to encode in token
 * @returns {string} - Generated token
 */
const generateToken = (user) => {
  const header = {
    alg: "HS256",
    typ: "JWT",
  };

  const payload = {
    userId: user.id,
    email: user.email,
    role: user.role || user.type_utilisateur || user.type,
    type: user.role || user.type_utilisateur || user.type, // Legacy support
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60, // 24 hours
  };

  // Simple base64 encoding (not secure for production)
  const encodedHeader = btoa(JSON.stringify(header));
  const encodedPayload = btoa(JSON.stringify(payload));

  return `${encodedHeader}.${encodedPayload}.fake-signature`;
};

export {
  isTokenValid,
  setSession,
  getCurrentUser,
  hasPermission,
  getTokenExpiration,
  generateToken,
  getActiveToken,
};
